import { create } from "zustand";

import { discardAnalytics } from "~/lib/analytics";
import {
  ApiError,
  claimMembershipInvite,
  deleteMe,
  fetchAuthConfig,
  fetchMe,
  updateMe,
  verifyOtp,
} from "~/lib/api";
import {
  endSession,
  establishSession,
  setAuthProvider,
} from "~/lib/auth-session";
import { loadToken } from "~/lib/auth-token";
import {
  activatePushNotifications,
  disablePushNotifications,
  discardPushNotifications,
} from "~/lib/push-notifications";
import { queryClient, queryKeys } from "~/lib/query-client";
import { cancelRoutineReminders } from "~/lib/routine-reminders";
import type { AuthConfig, AuthUser, UserUpdate } from "~/types/auth";

type AuthStatus = "loading" | "authenticated" | "unauthenticated";

function clearUserScopedQueries(): void {
  queryClient.removeQueries({ queryKey: queryKeys.userScoped });
}

/**
 * The account has left this device, so its reminders go with it — a shared phone must
 * not keep ringing one member's routine for the next. Called only on a real departure:
 * a boot that merely failed offline keeps them, because the member set them and the
 * phone should ring without a network.
 */
function silenceReminders(): void {
  void cancelRoutineReminders().catch(() => undefined);
}

interface AuthState {
  status: AuthStatus;
  user: AuthUser | null;
  /** What the sign-in screen needs to render. Null until `hydrate` has run. */
  config: AuthConfig | null;

  /** On boot: load the auth config and validate any persisted token. */
  hydrate: () => Promise<void>;
  /** Exchange a one-time code for a session and load the member. */
  verify: (email: string, code: string) => Promise<void>;
  /** Claim an app-membership invitation and replace the cached member. */
  claimInvite: (token: string) => Promise<void>;
  updateProfile: (update: UserUpdate) => Promise<void>;
  signOut: () => Promise<void>;
  /** Permanently delete the app account, then discard the local session. */
  deleteAccount: () => Promise<void>;
  /** Called when the API reports the session is no longer valid. */
  handleUnauthorized: () => void;
  /** Re-read the member (best effort) — after returning from the storefront. */
  refreshMembership: () => Promise<void>;
  /** Called when the API answers 403: the cached membership status is stale. */
  markMembershipRequired: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: "loading",
  user: null,
  config: null,

  hydrate: async () => {
    // The config drives the sign-in screen even when nobody is signed in, so a
    // failure here must not be fatal — the screen falls back to its defaults.
    const config = await fetchAuthConfig().catch(() => null);
    if (config) setAuthProvider(config.auth_provider);

    const token = await loadToken();
    if (!token) {
      clearUserScopedQueries();
      void discardPushNotifications();
      void discardAnalytics();
      silenceReminders();
      set({ status: "unauthenticated", user: null, config });
      return;
    }

    try {
      const user = await fetchMe();
      clearUserScopedQueries();
      set({ status: "authenticated", user, config });
      void activatePushNotifications(user.id).catch(() => undefined);
    } catch (error) {
      // Only a rejected credential is a reason to throw the session away. A network
      // blip or a 5xx says nothing about whether the token is still good, and
      // discarding it there would sign the member out for the airport wi-fi.
      if (error instanceof ApiError && error.status === 401) {
        await endSession();
        silenceReminders();
      }
      clearUserScopedQueries();
      void discardPushNotifications();
      void discardAnalytics();
      set({ status: "unauthenticated", user: null, config });
    }
  },

  verify: async (email, code) => {
    const session = await verifyOtp(email, code);
    await establishSession(session);
    clearUserScopedQueries();
    // The verify response already carries the member, so no second round-trip.
    set({ status: "authenticated", user: session.user });
    void activatePushNotifications(session.user.id).catch(() => undefined);
  },

  claimInvite: async (token) => {
    set({ user: await claimMembershipInvite(token) });
  },

  updateProfile: async (update) => {
    set({ user: await updateMe(update) });
  },

  signOut: async () => {
    clearUserScopedQueries();
    set({ status: "loading" });
    // Detach the token before dropping the old account session. A shared device must
    // not keep receiving that member's private specialist replies after sign-out.
    await disablePushNotifications().catch(() => undefined);
    silenceReminders();
    await endSession();
    set({ status: "unauthenticated", user: null });
  },

  deleteAccount: async () => {
    const previousUser = get().user;
    clearUserScopedQueries();
    set({ status: "loading" });
    await disablePushNotifications().catch(() => undefined);
    try {
      await deleteMe();
    } catch (error) {
      // `request()` broadcasts an authenticated 401 synchronously before throwing.
      // Never resurrect that expired identity merely because account deletion was
      // the request that discovered it.
      if (
        get().status === "unauthenticated" ||
        (error instanceof ApiError && error.status === 401)
      ) {
        clearUserScopedQueries();
        void discardPushNotifications();
        void discardAnalytics();
        silenceReminders();
        await endSession();
        set({ status: "unauthenticated", user: null });
        throw error;
      }
      set({ status: "authenticated", user: previousUser });
      if (previousUser)
        void activatePushNotifications(previousUser.id).catch(() => undefined);
      throw error;
    }
    silenceReminders();
    await endSession();
    set({ status: "unauthenticated", user: null });
  },

  handleUnauthorized: () => {
    if (get().status === "unauthenticated") return;
    clearUserScopedQueries();
    void discardPushNotifications();
    void discardAnalytics();
    silenceReminders();
    void endSession();
    set({ status: "unauthenticated", user: null });
  },

  refreshMembership: async () => {
    if (get().status !== "authenticated") return;
    try {
      set({ user: await fetchMe() });
    } catch {
      // Best effort — the member keeps whatever the store already shows.
    }
  },

  markMembershipRequired: () => {
    const { user } = get();
    if (!user) return;
    set({
      user: {
        ...user,
        membership_status: "none",
        has_membership_override: false,
        membership_override_granted_at: null,
      },
    });
  },
}));
